// src/patients/components/chart/timeline-lanes.tsx
//
// The Timeline's lane card, drawn as Samer's demo draws it (scrips-clinician-demo `?view=timeline`, ported through Antonin's
// catalog TimelineSwimlane, signal-ds feat/signal): a 160 label column, 28 high lanes, a 20 chip row and a 24 axis, the year
// ticks and a "Today" line, the ten mark shapes with a count beside a cluster, medication bars with the name above them, the
// plan as a dashed bar. One SVG at 1:1 pixels (its width measured), so the type sizes are the real ones. Colours are the
// contract's tokens as SVG attributes; sizes are SVG geometry. A lane with nothing in the window is left out (the demo's rule);
// a lane whose read failed stays, and says so.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  axisTicks,
  markShape,
  weekOf,
  type MarkShape,
  type TimelineBar,
  type TimelineLane,
  type TimelineMark,
  type TimelineRange,
} from '@/patients/lib/chart-timeline';

const LABEL_W = 160;
const LANE_H = 28;
const CHIP_ROW_H = 20;
const AXIS_H = 24;
const MED_TRACK = 22;
const MED_LABEL_H = 14;
const MED_BAR_H = 6;
const PLAN_BAR_H = 16;
const RIGHT_GUTTER = 28;
const FACE = 16;
const COUNT_GAP = 2;

/** Each shape's fill and edge (the demo's PAINT), contract tokens only. */
const PAINT: Record<MarkShape, { fill: string; edge: string; dashed?: boolean }> = {
  dot: { fill: 'var(--accent-primary)', edge: 'var(--accent-primary)' },
  ring: { fill: 'var(--accent-active-bg)', edge: 'var(--accent-primary)' },
  up: { fill: 'var(--status-attention)', edge: 'var(--status-attention-text)' },
  down: { fill: 'var(--status-attention)', edge: 'var(--status-attention-text)' },
  alert: { fill: 'var(--status-bad)', edge: 'var(--status-bad)' },
  square: { fill: 'var(--accent-primary)', edge: 'var(--accent-primary)' },
  'hollow-dot': { fill: 'var(--surface-read)', edge: 'var(--accent-primary)', dashed: true },
  'hollow-square': { fill: 'var(--surface-read)', edge: 'var(--accent-primary)', dashed: true },
};

/** One mark's face in a 16 box (the demo's shapes and stroke widths). */
export function MarkGlyph({ shape }: { shape: MarkShape }) {
  const p = PAINT[shape];
  const common = { fill: p.fill, stroke: p.edge, strokeWidth: 1.75, strokeDasharray: p.dashed ? '2.4 1.6' : undefined };
  switch (shape) {
    case 'dot':
    case 'ring':
    case 'hollow-dot':
      return <circle data-shape={shape} cx="8" cy="8" r={shape === 'dot' ? 6 : 6.1} {...common} strokeWidth={shape === 'ring' ? 2 : 1.75} />;
    case 'square':
    case 'hollow-square':
      return <rect data-shape={shape} x="2" y="2" width="12" height="12" rx="2.5" {...common} />;
    case 'up':
      return <path data-shape={shape} d="M8 1.6 L14.6 13.8 L1.4 13.8 Z" {...common} strokeWidth={1.25} strokeLinejoin="round" />;
    case 'down':
      return <path data-shape={shape} d="M8 14.4 L14.6 2.2 L1.4 2.2 Z" {...common} strokeWidth={1.25} strokeLinejoin="round" />;
    case 'alert':
      return (
        <g data-shape={shape}>
          <circle cx="8" cy="8" r="7" fill={p.fill} stroke={p.edge} strokeWidth={1} />
          <rect x="7.1" y="3.6" width="1.8" height="5.4" rx="0.9" fill="var(--surface-read)" />
          <circle cx="8" cy="11.5" r="1.05" fill="var(--surface-read)" />
        </g>
      );
  }
}

/** A standalone 16 icon of a shape (the legend). */
export function MarkIcon({ shape, size = FACE }: { shape: MarkShape; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden className="block flex-none overflow-visible">
      <MarkGlyph shape={shape} />
    </svg>
  );
}

let measureCtx: CanvasRenderingContext2D | null = null;
/** A label's drawn width at 12 px (jsdom has no canvas: an estimate there). */
/** The font the labels are drawn in, read from the drawn probe (`text-label`) once the card is on screen; a fixed guess before. */
let labelFont = '600 12px Inter, system-ui, sans-serif';
function textWidth(t: string): number {
  if (typeof document === 'undefined') return t.length * 7;
  try {
    measureCtx ??= document.createElement('canvas').getContext('2d');
  } catch {
    measureCtx = null;
  }
  if (!measureCtx) return t.length * 7;
  measureCtx.font = labelFont;
  return Math.ceil(measureCtx.measureText(t).width);
}

const RANK = { normal: 0, attention: 1, critical: 2 } as const;
/** A group's face: a result beats an order still waiting for one, then the worst flag. */
const faceRank = (m: TimelineMark) => (m.kind === 'pending' ? 0 : 10) + RANK[m.severity];
/** The demo's grouping: marks of one lane in the same (clinic) week share one mark, and marks that would touch on screen merge
 *  behind one count. The face is a result before a pending order, then the worst flag. */
export function clusters(marks: readonly TimelineMark[], x: (t: number) => number) {
  const byWeek = new Map<number, TimelineMark[]>();
  for (const m of [...marks].sort((a, b) => a.t - b.t)) {
    const k = weekOf(m.t);
    const g = byWeek.get(k);
    if (g) g.push(m);
    else byWeek.set(k, [m]);
  }
  // Merging is ANCHORED at a cluster's first week: a week joins only while it would touch that first mark and its count. The
  // demo's chained merge let weekly visits link up into one mark spanning 18 months (dev, 4 Nov: 166 visits, one mark).
  const groups: { x: number; items: TimelineMark[] }[] = [];
  for (const items of byWeek.values()) {
    const at = x(items[0].t);
    const cur = groups[groups.length - 1];
    if (cur) {
      const reach = cur.x + FACE / 2 + COUNT_GAP + textWidth(String(cur.items.length + items.length)) + COUNT_GAP + FACE / 2;
      if (at < reach) {
        cur.items.push(...items);
        continue;
      }
    }
    groups.push({ x: at, items: [...items] });
  }
  return groups.map((g) => ({
    x: g.x,
    items: g.items,
    face: g.items.reduce((w, m) => (faceRank(m) > faceRank(w) ? m : w), g.items[0]),
    /** Every item is after today: the group is faded. */
    future: (now: number) => g.items.every((m) => m.t > now),
  }));
}

/** Bars packed onto tracks so their labels never overlap (the demo's packing, 4 px apart). */
export function packBars(bars: readonly TimelineBar[], x: (t: number) => number, now: number, trackW: number) {
  const geo = bars.map((b) => {
    const x0 = Math.max(0, x(b.start));
    const x1 = Math.min(trackW, Math.max(x(b.end ?? now), x0 + 4));
    let label = b.label;
    let w = textWidth(label);
    if (w > trackW) {
      const words = label.split(/\s+/);
      while (words.length > 1 && textWidth(words.join(' ')) > trackW) words.pop();
      label = words.join(' ');
      w = textWidth(label);
    }
    const left = Math.max(0, Math.min(x0, trackW - w));
    return { bar: b, x0, x1, label, left, extent: [Math.min(x0, left), Math.max(x1, left + w)] as const };
  });
  const ends: number[] = [];
  const track = new Map<string, number>();
  for (const g of [...geo].sort((p, q) => p.extent[0] - q.extent[0])) {
    let i = ends.findIndex((e) => e + 4 <= g.extent[0]);
    if (i === -1) {
      i = ends.length;
      ends.push(g.extent[1]);
    } else ends[i] = g.extent[1];
    track.set(g.bar.id, i);
  }
  return { geo, track, tracks: Math.max(1, ends.length) };
}

export interface TimelineLanesProps {
  lanes: readonly { id: TimelineLane; title: string }[];
  marks: readonly TimelineMark[];
  bars: readonly TimelineBar[];
  range: TimelineRange;
  from: number;
  to: number;
  now: number;
  /** Lanes whose read failed: they stay, with a line saying so. */
  failed: Readonly<Record<TimelineLane, boolean>>;
  /** Lanes whose read has not answered yet: they stay, saying so, while the others draw (dev, 4 Nov: the visits read takes ~8 s). */
  loading?: Readonly<Partial<Record<TimelineLane, boolean>>>;
}

export function TimelineLanes({ lanes, marks, bars, range, from, to, now, failed, loading = {} }: TimelineLanesProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const probeRef = useRef<SVGTextElement>(null);
  const [, setFontRead] = useState(false);
  useLayoutEffect(() => {
    // Labels are measured in the font they are drawn in (the dev check on 4 Nov: a fixed guess let names run past the card).
    const probe = probeRef.current;
    if (!probe || typeof getComputedStyle === 'undefined') return;
    const cs = getComputedStyle(probe);
    if (cs.fontSize && cs.fontFamily) {
      labelFont = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      setFontRead(true);
    }
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setWidth(Math.max(320, el.clientWidth));
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const trackW = width - LABEL_W;
  const x = (t: number) => ((t - from) / (to - from || 1)) * (trackW - RIGHT_GUTTER);
  const inRange = (t: number) => t >= from && t <= to;
  const meds = packBars(bars.filter((b) => b.lane === 'medications' && b.start <= to && (b.end ?? now) >= from), x, now, trackW);
  const plans = packBars(bars.filter((b) => b.lane === 'plan' && b.start <= to && (b.end ?? now) >= from), x, now, trackW);
  const laneMarks = (id: TimelineLane) => marks.filter((m) => m.lane === id && inRange(m.t));
  const has = (id: TimelineLane) =>
    failed[id] || !!loading[id] || (id === 'medications' ? meds.geo.length > 0 : id === 'plan' ? plans.geo.length > 0 : laneMarks(id).length > 0);
  const shown = lanes.filter((l) => has(l.id));
  const laneH = (id: TimelineLane) =>
    failed[id] || loading[id] ? LANE_H : id === 'medications' ? Math.max(LANE_H, 4 + meds.tracks * MED_TRACK) : id === 'plan' ? Math.max(LANE_H, plans.tracks * LANE_H) : LANE_H;
  const tops = shown.reduce<number[]>((acc, _l, i) => [...acc, i === 0 ? 0 : acc[i - 1] + laneH(shown[i - 1].id)], []);
  const bodyTop = CHIP_ROW_H + AXIS_H;
  const bodyH = Math.max(LANE_H, shown.reduce((s, l) => s + laneH(l.id), 0));
  const height = bodyTop + bodyH;
  const tx = (t: number) => LABEL_W + x(t);
  const todayIn = inRange(now);
  // A year label too close to "Today" is left out (the demo's rule); one after today stays.
  const ticks = axisTicks(from, to, range).filter(
    (t) => !todayIn || Math.abs(x(t.t) - x(now)) > textWidth(t.label) / 2 + 44 || x(t.t) > x(now) + 40,
  );
  const todayLabelEnd = x(now) + 40 > trackW;

  const lane = (id: TimelineLane, top: number, h: number): ReactNode => {
    if (loading[id]) {
      return (
        <text className="ct-empty text-meta fill-text-secondary" x={LABEL_W + 8} y={top + h / 2 + 4}>
          Loading…
        </text>
      );
    }
    if (failed[id]) {
      return (
        <text className="ct-empty text-meta fill-text-secondary" x={LABEL_W + 8} y={top + h / 2 + 4}>
          Couldn&apos;t load this lane.
        </text>
      );
    }
    if (id === 'medications' || id === 'plan') {
      const set = id === 'medications' ? meds : plans;
      return set.geo.map((g) => {
        const tr = set.track.get(g.bar.id) ?? 0;
        const tip = `${g.bar.label}${g.bar.end === null ? ' · still on the list' : g.bar.endComputed ? ' · end computed from the recorded duration' : ''}`;
        if (id === 'plan') {
          const y = top + tr * LANE_H + (LANE_H - PLAN_BAR_H) / 2;
          const inside = g.x1 - g.x0 >= textWidth(g.label) + 16;
          return (
            <g key={g.bar.id} data-bar="plan" className="ct-bar" tabIndex={0} aria-label={tip}>
              <title>{tip}</title>
              <rect x={LABEL_W + g.x0} y={y} width={Math.max(8, g.x1 - g.x0)} height={PLAN_BAR_H} rx={4} fill="var(--accent-active-bg)" stroke="var(--accent-primary)" strokeDasharray="3 2" />
              <text className="text-label" fill="var(--accent-active-text)" x={inside ? LABEL_W + g.x0 + 8 : LABEL_W + g.left} y={y + 12}>
                {g.label}
              </text>
            </g>
          );
        }
        const y = top + 4 + tr * MED_TRACK;
        return (
          <g key={g.bar.id} data-bar="medication" className={g.bar.endComputed ? 'ct-bar ct-computed' : 'ct-bar'} tabIndex={0} aria-label={tip}>
            <title>{tip}</title>
            <text className="text-label fill-text-primary" x={LABEL_W + g.left} y={y + 11}>
              {g.label}
            </text>
            <rect
              x={LABEL_W + g.x0}
              y={y + MED_LABEL_H + 2}
              width={Math.max(4, g.x1 - g.x0)}
              height={MED_BAR_H}
              rx={3}
              fill="var(--cat-sky-100)"
              stroke="var(--cat-sky-500)"
              strokeDasharray={g.bar.endComputed ? '3 2' : undefined}
            />
          </g>
        );
      });
    }
    return clusters(laneMarks(id), x).map((c) => {
      const shape = markShape(c.face);
      const tip = c.items.map((m) => m.label).join('\n');
      const cx = LABEL_W + c.x;
      const cy = top + h / 2;
      const future = c.future(now);
      return (
        <g
          key={c.items[0].id}
          data-mark={c.face.kind}
          data-shape={shape}
          className={`ct-mark ${c.face.severity === 'critical' ? 'ct-critical' : c.face.severity === 'attention' ? 'ct-attention' : ''}`}
          tabIndex={0}
          aria-label={c.items.length > 1 ? `${c.items.length} items: ${c.items.map((m) => m.label).join('; ')}` : c.items[0].label}
          opacity={future ? 0.4 : undefined}
        >
          <title>{tip}</title>
          <svg x={cx - FACE / 2} y={cy - FACE / 2} width={FACE} height={FACE} viewBox="0 0 16 16" overflow="visible">
            <MarkGlyph shape={shape} />
          </svg>
          {c.items.length > 1 ? (
            <text className="ct-count text-label" fill="var(--ink-secondary)" x={cx + FACE / 2 + COUNT_GAP} y={cy + 4}>
              {c.items.length}
            </text>
          ) : null}
        </g>
      );
    });
  };

  return (
    <div ref={ref} data-slot="ct-chart" className="overflow-hidden rounded-lg border border-border-card bg-surface-card">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        role="img"
        aria-label={`Timeline from ${new Date(from).getUTCFullYear()} to ${new Date(to).getUTCFullYear()}`}
        className="block"
      >
        <text ref={probeRef} aria-hidden className="text-label" x={-1000} y={-1000} opacity={0}>
          M
        </text>
        {/* the axis row and the label column's corner */}
        <line x1={0} x2={width} y1={bodyTop - 0.5} y2={bodyTop - 0.5} stroke="var(--surface-line)" />
        {ticks.map((t) => (
          <g key={t.t}>
            <line className="ct-grid" x1={tx(t.t)} x2={tx(t.t)} y1={bodyTop} y2={height} stroke="var(--surface-app)" />
            <text className="ct-axis text-meta fill-text-secondary" x={tx(t.t)} y={CHIP_ROW_H + 16} textAnchor="middle">
              {t.label}
            </text>
          </g>
        ))}
        {todayIn ? (
          <g>
            <line className="ct-today" x1={tx(now)} x2={tx(now)} y1={bodyTop} y2={height} stroke="var(--accent-primary)" />
            <text
              className="ct-axis ct-today-label text-label"
              fill="var(--accent-active-text)"
              x={todayLabelEnd ? tx(now) - 4 : tx(now) + 4}
              y={CHIP_ROW_H + 16}
              textAnchor={todayLabelEnd ? 'end' : 'start'}
            >
              Today
            </text>
          </g>
        ) : null}
        {shown.map((l, i) => {
          const top = bodyTop + tops[i];
          const h = laneH(l.id);
          return (
            <g key={l.id} data-lane={l.id}>
              <line className="ct-row" x1={0} x2={width} y1={top + h - 0.5} y2={top + h - 0.5} stroke="var(--surface-line)" />
              <text className="ct-lane text-body-strong fill-text-primary" x={16} y={top + h / 2 + 5}>
                {l.title}
              </text>
              {lane(l.id, top, h)}
            </g>
          );
        })}
        {shown.length === 0 ? (
          <text className="ct-empty text-meta fill-text-secondary" x={16} y={bodyTop + 18}>
            Nothing in this period.
          </text>
        ) : null}
      </svg>
    </div>
  );
}
